import React, { useContext } from 'react';
import { WorkflowConversationPresentation } from './WorkflowConversationPresentation';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

type Props = {
  title: string;
  subtitle: string;
  /** A launcher-owned utility such as the completion-sound toggle. */
  action?: React.ReactNode;
  /** Lets a controller retain an existing workflow-status chip beside the utility. */
  trailing?: React.ReactNode;
  /** Matches the surrounding page hierarchy when the banner is embedded. */
  headingComponent?: 'h2' | 'h3';
  headingRef?: React.Ref<HTMLHeadingElement>;
};

/**
 * The shared Home Chat header treatment. Workflow controllers use this rather
 * than recreating a title row, so the launcher-owned utility stays aligned
 * with the title instead of floating above an embedded workflow surface.
 */
const HomeChatWorkflowBanner: React.FC<Props> = ({
  title,
  subtitle,
  action,
  trailing,
  headingComponent = 'h2',
  headingRef,
}) => {
  const { minimal } = useContext(WorkflowConversationPresentation);
  if (minimal) return null;
  return (
  <Box
    data-testid="home-chat-workflow-banner"
    data-home-chat-banner="true"
    sx={{
      px: 2,
      py: 1.5,
      borderBottom: 1,
      borderColor: 'divider',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 12rem' }}>
        <Typography ref={headingRef} component={headingComponent} variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {subtitle}
        </Typography>
      </Box>
      {(trailing || action) ? (
        <Box
          data-testid="home-chat-workflow-banner-actions"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            ml: 'auto',
            flex: '0 0 auto',
          }}
        >
          {trailing}
          {action}
        </Box>
      ) : null}
    </Box>
  </Box>
);
};

export default HomeChatWorkflowBanner;
